<template>
  <div>
    <RecycleScroller
      ref="recycleScroller"
      :items="allContacts"
      :item-height="140"
      :buffer="600"
      class="scroller"
      page-mode
      emit-update
      @update="onRecycleScrollerUpdated"
    >
      <VCard
        slot-scope="{ item, index }"
        :to="item.route"
        color="grey lighten-4"
        ripple
        class="grey--text text--darken-3"
        @click.prevent="onCardClicked(item)"
      >
        <VContainer
          fluid
          grid-list-xs
        >
          <VLayout
            align-center
            justify-center
            row
            wrap
          >
            <VFlex
              xs6
              sm5
              md4
              lg3
            >
              <VLayout
                row
                wrap
                align-center
                fill-height
              >
                <VFlex
                  v-if="selectable"
                  xs3
                >
                  <Transition name="fade">
                    <VCheckbox
                      v-if="selectable"
                      :input-value="inputValue(item)"
                      on-icon="$vuetify.icons.circleCheckboxOn"
                      off-icon="$vuetify.icons.circleCheckboxOff"
                      @click.stop="onCheckBoxClicked(item, index)"
                    />
                  </Transition>
                </VFlex>
                <VFlex
                  :xs9="selectable"
                  :xs12="!selectable"
                >
                  <VLayout
                    column
                  >
                    <VImg
                      class="avatar"
                      max-width="100px"
                      max-height="100px"
                      aspect-ratio="1.0"
                      contain
                      :class="{ gray: item.pendingStatus }"
                      :src="contactImgByIndex(index)"
                      :lazy-src="require('@/assets/sndle-avatar-default.png')"
                    >
                      <VLayout
                        slot="placeholder"
                        fill-height
                        align-center
                        justify-center
                        ma-0
                      >
                        <VProgressCircular
                          indeterminate
                          color="grey lighten-5"
                        />
                      </VLayout>
                    </VImg>
                    <div
                      v-if="item.pendingStatus"
                      class="img-description"
                    >
                      <div
                        class="caption font-weight-bold"
                        :class="{ 'red--text': item.pendingStatus }"
                      >
                        {{ contactPendingMessage(index) }}
                      </div>
                    </div>
                  </VLayout>
                </VFlex>
              </VLayout>
            </VFlex>
            <VFlex
              xs6
              sm7
              md8
              lg9
            >
              <VLayout
                row
                wrap
                justify-space-between
              >
                <VFlex
                  xs10
                >
                  <VCardTitle primary-title>
                    <VLayout
                      row
                      wrap
                    >
                      <VFlex xs12>
                        <div
                          class="title text-truncate"
                          :class="{
                            'indigo--text': !item.pendingStatus,
                            'text--darken-4': !item.pendingStatus }"
                        >
                          {{ item.name }} {{ item.lastName }}
                        </div>
                        <div
                          v-if="contactTitleByIndex(index)||contactOrganizationByIndex(index)"
                          class="font-italic caption font-weight-regular text-truncate"
                        >
                          {{ contactTitleByIndex(index) }} at {{ contactOrganizationByIndex(index) }}
                        </div>
                        <div class="body-1 font-weight-regular text-truncate grey--text text--darken-4">
                          {{ item.lastSmsBody }}
                        </div>
                      </VFlex>
                    </VLayout>
                  </VCardTitle>
                </VFlex>
                <VFlex
                  xs2
                >
                  <VLayout
                    column
                    align-end
                    justify-space-between
                    fill-height
                  >
                    <VBtn
                      small
                      icon
                      :to="{name: 'editContact', params: { contactId: item.id }}"
                    >
                      <VIcon>info_outline</VIcon>
                    </VBtn>
                    <VBtn
                      v-if="archiveContact"
                      small
                      icon
                      @click.stop="onArchiveClicked(index)"
                    >
                      <VIcon>mdi-archive</VIcon>
                    </VBtn>
                    <VBtn
                      v-if="swipeContact"
                      small
                      icon
                      @click.stop="onSwipeClicked(index)"
                    >
                      <VIcon>mdi-delete-sweep</VIcon>
                    </VBtn>
                    <VBtn
                      v-if="followup"
                      small
                      icon
                      @click.stop="onFollowupClicked(index)"
                    >
                      <VIcon>mdi-calendar-star</VIcon>
                    </VBtn>
                  </VLayout>
                </VFlex>
              </VLayout>
            </VFlex>
          </VLayout>
        </VContainer>
      </VCard>
    </RecycleScroller>
    <ContactFollowupRuleDialog
      v-if="dialog"
      :name="dialogName"
      :show-details="showDetails"
      :profile-id="dialogProfileId"
      :dialog.sync="dialog"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { contactMapGetters } from '@/store/utils'
import ContactFollowupRuleDialog from '@/components/automation/ContactFollowupRuleDialog'
export default {
  name: 'ContactTable',
  components: { ContactFollowupRuleDialog },
  props: {
    selectable: {
      type: Boolean,
      default: false
    },
    selectAll: {
      type: Boolean,
      default: false
    },
    archiveContact: {
      type: Boolean,
      default: false
    },
    selectedIds: {
      type: Set,
      default: function () {
        return new Set()
      }
    },
    unselectedIds: {
      type: Set,
      default: function () {
        return new Set()
      }
    },
    swipeContact: {
      type: Boolean,
      default: false
    },
    followup: {
      type: Boolean,
      default: true
    }
  },
  data: function () {
    return {
      isIndeterminate: false,
      dialog: false,
      showDetails: false,
      dialogName: '',
      dialogProfileId: 0
    }
  },
  computed: {
    ...mapGetters(Object.assign(contactMapGetters,
      { messageLoading: 'message/getLoadingStatus' },
      { contactSelected: 'contact/getContactSelectedByIndex' },
      { contactPendingMessage: 'contact/getPendingMessageByIndex' }
    )),
    selectedContacts: {
      get () {
        return this.selectedIds
      },
      set (val) {
        this.$emit('update:selectedIds', val)
      }
    },
    unselectedContacts: {
      get () {
        return this.unselectedIds
      },
      set (val) {
        this.$emit('update:unselectedIds', val)
      }
    }
  },
  watch: {
    selectAll: function () {
      this.selectedContacts = new Set()
      this.unselectedContacts = new Set()
    }
  },
  methods: {
    ...mapActions({
      setMsgDrawerVisibility: 'messageDrawer/setVisibility',
      setMsgContact: 'messageDrawer/setItem',
      setContactSelected: 'contact/setContactSelectedByIndex'
    }),
    onRecycleScrollerUpdated (startIndex, endIndex) {
      if (this.allContacts.length !== 0 && endIndex === this.allContacts.length && endIndex !== this.contactTotalCount) {
        this.$emit('fetchingMore')
      }
    },
    onCardClicked (item) {
      this.setMsgDrawerVisibility({ val: true })
      if (!this.messageLoading) this.setMsgContact({ val: item })
    },
    onArchiveClicked (index) {
      this.$store.dispatch('contact/archiveContactByIndex', { index })
    },
    onSwipeClicked (index) {
      // console.log('onSwipeClicked')
    },
    onFollowupClicked (index) {
      if (index < 0 || index >= this.allContacts.length) {
        return
      }
      const contact = this.allContacts[index]
      if (contact) {
        this.dialogName = `${contact.name} ${contact.lastName}`
        this.dialogProfileId = Number(contact.id)
        this.dialog = true
      }
    },
    inputValue (item) {
      return this.selectAll ? !this.unselectedContacts.has(item.id) : this.selectedContacts.has(item.id)
    },
    onCheckBoxClicked (item) {
      return this.selectAll ? this.toggleItems('unselectedContacts', item) : this.toggleItems('selectedContacts', item)
    },
    toggleItems (name, item) {
      const newSet = new Set(this[name])
      if (this[name].has(item.id)) {
        newSet.delete(item.id)
      } else {
        newSet.add(item.id)
      }
      this[name] = newSet
    }
  }
}
</script>

<style lang="scss" scoped>
  .scroller {
    height: 100%;
  }
  .avatar {
    vertical-align: middle;
    -webkit-border-radius: 50%;
    -moz-border-radius: 50%;
    border-radius: 50%;
    -khtml-border-radius: 50%;
    border: 3px solid #f5f5f5;
    margin-top: 10px;
    object-fit: fill;
  }
  .gray {
    -webkit-filter: grayscale(100%);
    filter: grayscale(100%);
  }
  .img-description {
    text-align: center;
    width: 100px;
  }
  .text-gray {

  }
  .fluid {
    padding: 0px;
  }
  .fade-enter-active {
    transition: opacity .5s;
  }
  .fade-enter /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
  }
</style>
